import {
  colors,
  createStaticStyles,
  cssFns,
  useScreenDetector,
  useStaticStyles,
} from "@hermes/ui";
import { useInView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";

import { DoctorProfileImage } from "../doctor-card/profile-image";

export const DoctorsListLoading = ({
  length = 5,
  onInView,
}: {
  length?: number;
  onInView?: () => void;
}) => {
  return (
    <>
      {Array.from({ length }).map((_, index) => (
        <DoctorCardSkeleton key={index} onInView={onInView} />
      ))}
    </>
  );
};

const styles = {
  profileReviewImage: createStaticStyles({
    common: [
      cssFns.gridColumn(1, 2),
      {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
    ],
    mobile: [cssFns.gridRow(1, 2), { marginBlockEnd: 8 }],
    desktop: [cssFns.gridRow(1, 4), { marginBlockEnd: 12 }],
  }),
  profileReview: createStaticStyles({
    common: [
      cssFns.gridColumn(2, 3),
      cssFns.gridRow(1, 2),
      { marginBlockEnd: 16 },
    ],
  }),
  containerInner: createStaticStyles({
    common: {
      display: "grid",
      gridTemplateColumns: "auto 1fr",
    },
    desktop: [
      cssFns.padding(26, 20),
      {
        gridTemplateRows: "auto auto 1fr",
        columnGap: 30,
      },
    ],
    mobile: [
      cssFns.padding(10),
      {
        gridTemplateRows: "minmax(122px, auto) repeat(4, auto)",
        columnGap: 12,
      },
    ],
  }),
  mobileSeparator: createStaticStyles({
    common: [
      cssFns.borderBlockEnd({
        color: colors.principal.grayline,
        width: 1,
        style: "solid",
      }),
      cssFns.gridRow(2, 3),
      cssFns.gridColumn(1, 3),
      { marginBlock: 16 },
    ],
  }),
  container: createStaticStyles({
    mobile: [
      cssFns.border({
        width: 1,
        style: "solid",
        color: colors.principal.grayline,
      }),
      {
        gridTemplateColumns: "1fr",
      },
    ],
    desktop: {
      gridTemplateColumns: "1fr auto 330px",
    },
    common: {
      display: "grid",
      borderRadius: 12,
      backgroundColor: colors.principal.white,
      minHeight: 330,
    },
  }),
  doctorInfoBlock: createStaticStyles({
    mobile: [
      cssFns.gridColumn(1, 3),
      cssFns.gridRow(4, 5),
      { marginBlockEnd: 6 },
    ],
    desktop: [
      cssFns.gridColumn(2, 3),
      cssFns.gridRow(2, 3),
      { marginBlockEnd: 24 },
    ],
  }),
  clinicInfoBlock: createStaticStyles({
    common: cssFns.gridRow(3, 4),
    mobile: [cssFns.gridColumn(1, 3), { marginBlockEnd: 16 }],
    desktop: cssFns.gridColumn(2, 3),
  }),
  slotsBlock: createStaticStyles({
    common: [
      cssFns.borderInlineStart({
        color: colors.principal.grayline,
        width: 1,
        style: "solid",
      }),
    ],
    mobile: [
      cssFns.padding(16, 10),
      {
        paddingBlockStart: 0,
        alignSelf: "flex-end",
      },
    ],
    desktop: [cssFns.padding(20, 16)],
  }),
};

export const DoctorCardSkeleton = ({ onInView }: { onInView?: () => void }) => {
  const { isMobile } = useScreenDetector();
  const { staticStyles } = useStaticStyles();

  const view = useInView({
    // triggerOnce: true,
    threshold: 0,
    onChange(inView) {
      if (inView) {
        onInView?.();
      }
    },
  });

  return (
    <div ref={view.ref} css={staticStyles(styles.container)}>
      <div css={staticStyles(styles.containerInner)}>
        <div css={styles.profileReviewImage}>
          <DoctorProfileImage
            size={isMobile ? "80px" : "110px"}
            isClickable={false}
          />
        </div>
        <div css={staticStyles(styles.profileReview)}>
          <Skeleton css={{ height: 54, marginBlockEnd: 8 }} />
          <Skeleton css={{ height: 48 }} />
        </div>
        {isMobile && <div css={staticStyles(styles.mobileSeparator)} />}
        <div css={staticStyles(styles.doctorInfoBlock)}>
          <Skeleton css={{ height: 54 }} />
        </div>
        <div css={staticStyles(styles.clinicInfoBlock)} data-doctor>
          <div css={{ marginBlockEnd: 6 }}>
            <Skeleton css={{ height: isMobile ? 64 : 46 }} />
          </div>
        </div>
      </div>
      <div data-spacer />
      <div css={staticStyles(styles.slotsBlock)}>
        <Skeleton height={isMobile ? 59 : 120} />
      </div>
    </div>
  );
};
